import { useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "@/src/hooks/useApiClient";
import { useAppToast } from "@/src/hooks/useAppToast";
import { useTranslations } from "@/src/i18n/i18n";
import { invalidateApiPaths } from "@/src/libs/invalidateApiPaths";
import type { AccountFlagCode } from "@/src/libs/moderation.type";

/**
 * Poser et retirer les drapeaux d'un compte.
 *
 * ## Ce qu'un drapeau est, et n'est pas
 *
 * Une **note interne**, jamais une sanction : il n'ouvre ni ne ferme aucun
 * droit, et l'intéressé ne le voit nulle part — aucune route utilisateur ne lit
 * cette table, aucune notification n'est émise. C'est ce qui permet de marquer
 * un dossier sans avoir à trancher.
 *
 * ## Poser deux fois n'est pas une erreur
 *
 * Le serveur fait un `ON CONFLICT … DO UPDATE` : reposer un drapeau déjà là
 * réécrit sa note. L'interface n'a donc pas à distinguer « poser » de
 * « modifier », et un double clic ne récolte pas un 409.
 *
 * ## `pendingFlag`, et non `isPending`
 *
 * Il dit *quel* drapeau part vers le serveur. Avec un simple booléen, les huit
 * pastilles de l'écran se figeraient ensemble à chaque clic — même motif que le
 * `pendingId` de `useResolveReport`.
 */
export function useAccountFlags(accountId: number) {
  const $api = useApiClient();
  const queryClient = useQueryClient();
  const t = useTranslations("moderation");
  const { showSuccess, showApiError } = useAppToast();

  // Les deux chemins : un drapeau posé change la fiche (liste détaillée) **et**
  // la liste des comptes (pastilles). Nommer la seule liste laisserait la fiche
  // sur son état d'avant — cf. `invalidateApiPaths`.
  const rafraichir = () =>
    invalidateApiPaths(queryClient, [
      "/api/v1/moderation/accounts",
      "/api/v1/moderation/accounts/{id}",
    ]);

  const pose = $api.useMutation(
    "post",
    "/api/v1/moderation/accounts/{id}/flags",
    {
      onSuccess: () => {
        void rafraichir();
        showSuccess(t("flag-set"));
      },
      onError: (error) => showApiError(t("flag-error"), error),
    },
  );

  const retrait = $api.useMutation(
    "delete",
    "/api/v1/moderation/accounts/{id}/flags/{flag}",
    {
      onSuccess: () => {
        void rafraichir();
        showSuccess(t("flag-removed"));
      },
      onError: (error) => showApiError(t("flag-error"), error),
    },
  );

  return {
    setFlag: (flag: AccountFlagCode, note?: string) =>
      pose.mutate({
        params: { path: { id: accountId } },
        // Une note vide n'est pas envoyée : le serveur borne à `min(1)` et
        // refuserait la chaîne vide qu'un champ laissé blanc produit.
        body: { flag, ...(note?.trim() ? { note: note.trim() } : {}) },
      }),
    removeFlag: (flag: AccountFlagCode) =>
      retrait.mutate({ params: { path: { id: accountId, flag } } }),
    /**
     * Le drapeau dont l'écriture est en vol, `undefined` au repos.
     *
     * `body` est typé optionnel par le générateur — il ne l'est pas pour cette
     * route, mais le contrat ne sait pas le dire. Le `?.` évite d'affirmer ici
     * ce que `tsc` refuse de tenir pour acquis.
     */
    pendingFlag: pose.isPending
      ? pose.variables.body?.flag
      : retrait.isPending
        ? retrait.variables.params.path.flag
        : undefined,
  };
}
